import { createFilterOptions } from "@mui/material";
import User from "assets/img/svg/user";
import type { ContactRender } from "components/ImportData/CreateInvoiceModal";
import AutocompleteFormComponent from "components/shared/FormComponent/AutocompleteFormComponent";
import StyledButton from "components/shared/StyledButton";
import DropZoneCSV from "components/shared/UploadCsv";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { fetchBexioContacts } from "store/bexioStore";
import { uploadFile } from "store/documentStore";
import { useAppDispatch, useAppSelector } from "store/store";
import type { ITransferTermSale } from "store/transferTermSalesStore";
import { createSaleInvoice, ETransferTermSalesStatus, updateTransferTermSale } from "store/transferTermSalesStore";

import styles from "./style.module.scss";

interface ISaveInvoiceModal {
    /**
     * Term sale being updated.
     */
    termSale: ITransferTermSale | undefined;
    /**
     * Triggers closing of modal.
     */
    handleClose: ({
        updatedSale,
        updatedSales,
    }: {
        updatedSale?: ITransferTermSale;
        updatedSales?: ITransferTermSale[];
    }) => void;
    formMode: "new" | "edit";
    currentSales: ITransferTermSale[];
    /**
     * Whether company has Bexio connected.
     */
    hasBexio: boolean;
}

/**
 * Modal from TransferTermSaleTable to confirm sending invoice. If they add invoiceUrl, they can either do it manually or using Bexio if connected.
 */
const SaveInvoiceModal = ({ termSale, handleClose, formMode, currentSales, hasBexio }: ISaveInvoiceModal) => {
    const dispatch = useAppDispatch();
    const [t] = useTranslation();

    const { bexioContacts, fetchBexioContactsStatus } = useAppSelector((state) => state.bexio);

    const mappedContacts = useMemo(() => {
        return bexioContacts.map((contact) => {
            return {
                name: contact.name_1 + (contact.name_2 ? ` (${contact.name_2})` : ""),
                id: contact.id,
            };
        });
    }, [bexioContacts]);

    const [files, setFiles] = useState<File[]>();
    const [isFileLoading, setIsFileLoading] = useState(false);
    const [submitPending, setSubmitPending] = useState(false);
    const [bexioContact, setBexioContact] = useState<ContactRender>();

    const defaultValues = {
        invoiceUrl: "",
        contactId: null,
    };

    const { handleSubmit, setValue } = useForm({ defaultValues, mode: "all" });

    const onSubmit = async (data: any) => {
        setSubmitPending(true);

        if (termSale) {
            if (bexioContact) {
                const response = await dispatch(
                    createSaleInvoice({ contactId: bexioContact?.id, salesId: termSale.id }),
                );
                if (response.meta.requestStatus === "fulfilled") {
                    setSubmitPending(false);
                    handleClose({});
                } else {
                    setSubmitPending(false);
                }
            } else {
                const updatedSale = {
                    ...termSale,
                    invoiceUrl: data.invoiceUrl ?? "",
                    paymentDate: formMode === "edit" ? (termSale.paymentDate ? termSale.paymentDate : null) : null,
                    status: formMode === "edit" ? termSale.status : ETransferTermSalesStatus.WAITING_PAYMENT,
                };

                const response = await dispatch(updateTransferTermSale({ saleId: termSale.id, data: updatedSale }));
                if (response.meta.requestStatus === "fulfilled") {
                    setSubmitPending(false);

                    const newSales = currentSales.map((sale) => {
                        if (sale.id === updatedSale.id) {
                            return { ...updatedSale };
                        } else {
                            return sale;
                        }
                    });

                    // On creation, no need to update currentSale as we're not inside DocumentsDrawer
                    formMode === "new"
                        ? handleClose({ updatedSales: newSales })
                        : handleClose({ updatedSale: updatedSale });
                } else {
                    setSubmitPending(false);
                }
            }
        }
    };

    // Save url into invoiceUrl property once file chosen.
    useEffect(() => {
        if (files && files.length > 0) {
            setIsFileLoading(true);
            const file: File = files[0];
            const fileName = file.name ? file.name : "";
            const formData = new FormData();
            formData.append("file", file, fileName);

            dispatch(uploadFile({ formData })).then((resp) => {
                if (resp.meta.requestStatus === "fulfilled") {
                    setValue(`invoiceUrl`, resp.payload.imagePath, { shouldDirty: true });
                    setIsFileLoading(false);
                }
            });
        }
    }, [files]);

    useEffect(() => {
        if (fetchBexioContactsStatus === "idle" && hasBexio) {
            dispatch(fetchBexioContacts());
        }
    }, [fetchBexioContactsStatus]);

    const renderContactOption = (props: any, option: ContactRender) => (
        <li {...props} style={{ display: "flex", gap: "5px" }}>
            <User />
            <span>{option.name}</span>
        </li>
    );

    const filterContactOptions = createFilterOptions({
        matchFrom: "start",
        stringify: (option: any) => option.name,
    });

    return (
        <form className={styles["invoice-modal"]} onSubmit={handleSubmit(onSubmit)}>
            {formMode === "new" && hasBexio && (
                <div className={styles["bexio-option"]}>
                    <span>{t("TransferTerms.InvoiceModal.BexioInfo")}</span>
                    <AutocompleteFormComponent
                        labelStyle={{ fontSize: "13px", paddingLeft: "0px" }}
                        label={t("TransferTerms.InvoiceModal.ChooseContact")}
                        placeholder={t("ImportData.ContactSelectPlaceholder")}
                        options={mappedContacts}
                        renderOptionText={(option: ContactRender) => option.name}
                        renderOption={renderContactOption}
                        filterOptions={filterContactOptions}
                        onChange={(e, value) => {
                            setBexioContact(value);
                        }}
                        sx={{ marginBottom: "1rem" }}
                    />
                </div>
            )}

            {
                <div>
                    <span className={styles["dropzone-info"]}>
                        {hasBexio
                            ? t("TransferTerms.InvoiceModal.OrAttachPDF")
                            : t("TransferTerms.InvoiceModal.AttachPDF")}
                    </span>
                    <DropZoneCSV
                        setFile={setFiles}
                        file={files}
                        acceptedFilesTypeList={{ "application/pdf": [".pdf"] }}
                    />
                </div>
            }

            <StyledButton buttonType="primary" disabled={isFileLoading} type="submit" loading={submitPending}>
                {formMode === "new"
                    ? t("TransferTerms.InvoiceModal.ConfirmMailing")
                    : t("TransferTerms.InvoiceModal.SaveInvoice")}
            </StyledButton>
        </form>
    );
};

export default SaveInvoiceModal;
